import { cardLabel, newDecks, type Card } from '../cards';
import { compareScores, holdemHandName, HoldemRank, scoreSeven } from '../poker';
import { Rng } from '../rng';
import { luckOf, secondChanceFrame } from './luck';
import type { ActResult, CasinoGame, TableEnv, TableOption } from './types';

/**
 * Fixed-limit Texas Hold'em against two AI players (decided with the user
 * 2026-09-23): one hand is one play. Your bet is the big blind; the small
 * blind is half of it. Bets and raises are one big blind before the flop
 * and on the flop, two on the turn and river, with at most CAP bets a
 * street. The button moves every hand.
 *
 * The AIs are house-backed; only your money matters. You win the part of
 * the pot you can cover (each player's chips up to your own total), less
 * the rake — and like a real card room, no flop, no rake. If you can't
 * cover a call you go all in and the board runs out.
 *
 * AI moves play out on the real-time tick (AI_MS apart). The pane shows
 * your chance to win against random hands, and the share of the pot a call
 * needs, as a learning aid — the AIs' betting tells you the rest.
 */
const AI_MS = 900;
/** Bets a street can see (the bet plus three raises; the big blind counts). */
export const CAP = 4;
/**
 * rake: share of a pot you win taken by the house, once a flop is dealt.
 * Set by sim (holdem.sim.test.ts, 12,000 hands a cell): the best simple
 * strategy found (bet or raise at 1.3-1.5x your fair share of the win
 * chance, call when the pot odds say so, never bluff) returns ~100.5% at
 * 11% and ~98.8% at 13%; looser or more aggressive styles do worse.
 * secondChance: the ★'s luck, how often a river that would beat you is
 * dealt again, by luck level; calibrated by the same sim to 103% / 110%.
 * An object so the tuning sim can try values.
 */
export const HOLDEM_TUNING = { rake: 0.12, secondChance: [0, 0.3, 0.55] };
/** Win-chance samples for an AI's decision and for the pane's hint. */
const AI_SAMPLES = 120;
const HINT_SAMPLES = 400;

export interface Personality {
  name: string;
  /** Bets or raises when its win chance is this many times its fair share. */
  raiseAt: number;
  /** Calls a little worse than the pot's odds by this much. */
  callSlack: number;
  /** Chance of betting or calling on nothing. */
  bluff: number;
}

/** Mae plays tight and bets her good hands; Sal calls too much and bluffs. */
export const OPPONENTS: readonly Personality[] = [
  { name: 'Mae', raiseAt: 1.4, callSlack: 0, bluff: 0.04 },
  { name: 'Sal', raiseAt: 1.75, callSlack: 0.08, bluff: 0.12 },
];
const NAMES = ['You', ...OPPONENTS.map((o) => o.name)];
const STREETS = ['Preflop', 'Flop', 'Turn', 'River'];

export interface Seat {
  hole: Card[];
  folded: boolean;
  allIn: boolean;
  /** Chips put in this hand. */
  contrib: number;
  /** Chips put in this street. */
  street: number;
  /** Still to act before this street's betting closes. */
  pending: boolean;
}

export interface HoldemResult {
  how: 'showdown' | 'folds' | 'youFold';
  /** Hands at a showdown (null for a folded seat). */
  scores: (number[] | null)[];
  winners: number[];
}

export interface HoldemState {
  phase: 'idle' | 'betting' | 'done';
  seats: Seat[];
  board: Card[];
  /** Cards still to come, dealt from the end. */
  deck: Card[];
  street: number;
  /** Bets made this street (the big blind counts as one). */
  raises: number;
  turn: number;
  button: number;
  /** Recent actions, newest last. */
  log: string[];
  /** When the next AI move happens (ms). */
  nextAt: number;
  /** The big blind: your bet when the hand was dealt. */
  bet: number;
  /** Your chance to win against random hands for the players still in. */
  equity: number;
  returned: number;
  result: HoldemResult | null;
  /** The ★'s luck dealt the river again. */
  lucky: boolean;
}

/**
 * Your chance to win (ties split) against `opponents` random hands, by
 * sampling the unseen cards.
 */
export function winChance(hole: readonly Card[], board: readonly Card[], opponents: number, rng: Rng, samples: number): number {
  if (opponents <= 0) return 1;
  const known = new Set([...hole, ...board]);
  const rest: Card[] = [];
  for (let c = 0; c < 52; c++) if (!known.has(c)) rest.push(c);
  const need = 5 - board.length;
  const draw = opponents * 2 + need;
  let total = 0;
  for (let s = 0; s < samples; s++) {
    for (let i = 0; i < draw; i++) {
      const j = i + rng.int(rest.length - i);
      const t = rest[i]!;
      rest[i] = rest[j]!;
      rest[j] = t;
    }
    const full = board.concat(rest.slice(opponents * 2, draw));
    const mine = scoreSeven(hole.concat(full));
    let lost = false;
    let tied = 0;
    for (let o = 0; o < opponents && !lost; o++) {
      const cmp = compareScores(mine, scoreSeven([rest[o * 2]!, rest[o * 2 + 1]!, ...full]));
      if (cmp < 0) lost = true;
      else if (cmp === 0) tied++;
    }
    if (!lost) total += 1 / (tied + 1);
  }
  return total / samples;
}

export type HoldemAction = 'fold' | 'call' | 'raise';

/** An AI (or the sim player) decides from its win chance and the pot odds. */
export function decide(
  hole: readonly Card[],
  board: readonly Card[],
  opponents: number,
  toCall: number,
  pot: number,
  canRaise: boolean,
  who: Personality,
  rng: Rng,
): HoldemAction {
  const equity = winChance(hole, board, opponents, rng, AI_SAMPLES);
  const bluff = rng.next() < who.bluff;
  if (canRaise && (equity * (opponents + 1) >= who.raiseAt || bluff)) return 'raise';
  if (toCall === 0) return 'call';
  return equity + who.callSlack >= toCall / (pot + toCall) || bluff ? 'call' : 'fold';
}

const betSize = (s: HoldemState) => (s.street < 2 ? s.bet : s.bet * 2);
const topStreet = (s: HoldemState) => Math.max(...s.seats.map((x) => x.street));
const pot = (s: HoldemState) => s.seats.reduce((n, x) => n + x.contrib, 0);
const inHand = (s: HoldemState) => s.seats.map((_, i) => i).filter((i) => !s.seats[i]!.folded);
const cards = (cs: readonly Card[]) => cs.map(cardLabel).join(' ');

function nextPending(s: HoldemState, from: number): number {
  for (let step = 1; step <= s.seats.length; step++) {
    const i = (from + step) % s.seats.length;
    if (s.seats[i]!.pending) return i;
  }
  return -1;
}

/** Your win chance for the pane (its own RNG, so the deal isn't touched). */
function withEquity(s: HoldemState): HoldemState {
  const seed = [...s.seats[0]!.hole, ...s.board].reduce((h, c) => (h * 53 + c + 1) >>> 0, 17);
  const opponents = inHand(s).length - 1;
  return { ...s, equity: winChance(s.seats[0]!.hole, s.board, opponents, new Rng(seed), HINT_SAMPLES) };
}

function logged(s: HoldemState, line: string): string[] {
  return [...s.log, line].slice(-6);
}

/**
 * One player acts. Returns the new state and what you paid (only seat 0's
 * chips come from your bankroll).
 */
function applyAction(s: HoldemState, who: number, action: HoldemAction, bankroll: number): { s: HoldemState; paid: number } {
  const seats = s.seats.map((x) => ({ ...x }));
  const me = seats[who]!;
  const toCall = topStreet(s) - me.street;
  me.pending = false;
  if (action === 'fold' && toCall > 0) {
    me.folded = true;
    return { s: { ...s, seats, log: logged(s, `${NAMES[who]} ${who === 0 ? 'fold' : 'folds'}.`) }, paid: 0 };
  }
  if (action === 'raise' && s.raises < CAP) {
    const pay = toCall + betSize(s);
    me.contrib += pay;
    me.street += pay;
    for (const [i, x] of seats.entries()) if (i !== who && !x.folded && !x.allIn) x.pending = true;
    const word = topStreet(s) === 0 ? (who === 0 ? 'bet' : 'bets') : who === 0 ? 'raise to' : 'raises to';
    const line = `${NAMES[who]} ${word} $${me.street}`;
    return { s: { ...s, seats, raises: s.raises + 1, log: logged(s, line) }, paid: who === 0 ? pay : 0 };
  }
  // Call or check; you go all in if you can't cover it.
  const pay = who === 0 ? Math.min(toCall, bankroll) : toCall;
  me.contrib += pay;
  me.street += pay;
  if (who === 0 && pay < toCall) me.allIn = true;
  const line = pay === 0 ? `${NAMES[who]} ${who === 0 ? 'check' : 'checks'}.` : me.allIn ? `You're all in ($${pay}).` : `${NAMES[who]} ${who === 0 ? 'call' : 'calls'} $${pay}`;
  return { s: { ...s, seats, log: logged(s, line) }, paid: who === 0 ? pay : 0 };
}

/** The part of the pot you can win: each player's chips up to your own. */
export function yourPot(s: HoldemState): number {
  const mine = s.seats[0]!.contrib;
  return s.seats.reduce((n, x) => n + Math.min(x.contrib, mine), 0);
}

function settle(s: HoldemState, how: HoldemResult['how'], bankroll: number, frames: string[], now: number): ActResult<HoldemState> {
  const rake = s.board.length >= 3 ? HOLDEM_TUNING.rake : 0;
  const players = inHand(s);
  let result: HoldemResult;
  let returned = 0;
  let quads = false;
  if (how === 'youFold') result = { how, scores: [], winners: [] };
  else if (how === 'folds') {
    result = { how, scores: [], winners: players };
    if (players[0] === 0) returned = Math.floor(yourPot(s) * (1 - rake));
  } else {
    const scores = s.seats.map((x) => (x.folded ? null : scoreSeven([...x.hole, ...s.board])));
    const best = players.reduce((b, i) => (compareScores(scores[i]!, scores[b]!) > 0 ? i : b), players[0]!);
    const winners = players.filter((i) => compareScores(scores[i]!, scores[best]!) === 0);
    result = { how, scores, winners };
    if (winners.includes(0)) {
      returned = Math.floor((yourPot(s) / winners.length) * (1 - rake));
      quads = scores[0]![0]! >= HoldemRank.Quads;
    }
    // The cards turn over one player at a time, then the result.
    const head = `Board: ${cards(s.board)}`;
    const shown = players.filter((i) => i !== 0);
    for (let k = 1; k <= shown.length; k++) {
      frames.push([head, `You: ${cards(s.seats[0]!.hole)}`, ...shown.slice(0, k).map((i) => `${NAMES[i]}: ${cards(s.seats[i]!.hole)}`)].join('\n'));
    }
  }
  const wagered = s.seats[0]!.contrib;
  return {
    state: { ...s, phase: 'done', result, returned, turn: -1, nextAt: now },
    bankroll: bankroll + returned,
    settled: { wagered, returned },
    jackpot: quads,
    frames: frames.length ? frames : undefined,
  };
}

/** Would you lose a showdown on this board against the players still in? */
function losing(s: HoldemState, board: readonly Card[]): boolean {
  const mine = scoreSeven([...s.seats[0]!.hole, ...board]);
  return inHand(s).some((i) => i !== 0 && compareScores(scoreSeven([...s.seats[i]!.hole, ...board]), mine) > 0);
}

/**
 * After an action: move to the next player, deal the next street, or
 * settle. When you're all in, the rest of the board runs out.
 */
function advance(s: HoldemState, from: number, env: TableEnv, bankroll: number, frames: string[]): ActResult<HoldemState> {
  if (s.seats[0]!.folded) return settle(s, 'youFold', bankroll, frames, env.now);
  if (inHand(s).length === 1) return settle(s, 'folds', bankroll, frames, env.now);
  const next = nextPending(s, from);
  if (next >= 0) return { state: { ...s, turn: next, nextAt: env.now + AI_MS }, bankroll, frames: frames.length ? frames : undefined };
  if (s.street === 3) return settle(s, 'showdown', bankroll, frames, env.now);

  // Deal the next street.
  const deck = [...s.deck];
  let board = [...s.board];
  let lucky = false;
  const street = s.street + 1;
  if (street === 1) board.push(deck.pop()!, deck.pop()!, deck.pop()!);
  else {
    let card = deck.pop()!;
    // The ★'s luck: a river that would beat you is sometimes dealt again.
    if (street === 3 && losing(s, [...board, card]) && env.rng.next() < (HOLDEM_TUNING.secondChance[luckOf(env)] ?? 0)) {
      const first = card;
      card = deck.pop()!;
      lucky = !losing(s, [...board, card]);
      if (lucky) frames.push(secondChanceFrame(`River: ${cardLabel(first)}\n\n${cards([...board, first])}`));
    }
    board.push(card);
  }
  const seats = s.seats.map((x) => ({ ...x, street: 0, pending: !x.folded && !x.allIn }));
  const runOut = s.seats[0]!.allIn;
  if (runOut) for (const x of seats) x.pending = false;
  let dealt: HoldemState = { ...s, seats, board, deck, street, raises: 0, lucky: s.lucky || lucky };
  dealt = withEquity(dealt);
  // First to act after a deal is the first player left of the button.
  return advance(dealt, dealt.button, env, bankroll, frames);
}

function deal(s: HoldemState, env: TableEnv): ActResult<HoldemState> {
  const deck = env.rng.shuffle(newDecks(1));
  const button = (s.button + 1) % NAMES.length;
  const seats: Seat[] = NAMES.map(() => ({ hole: [deck.pop()!, deck.pop()!], folded: false, allIn: false, contrib: 0, street: 0, pending: true }));
  const small = Math.max(1, Math.floor(env.bet / 2));
  const blinds: [number, number][] = [
    [(button + 1) % NAMES.length, small],
    [(button + 2) % NAMES.length, env.bet],
  ];
  let bankroll = env.bankroll;
  for (const [i, amount] of blinds) {
    seats[i]!.contrib = amount;
    seats[i]!.street = amount;
    if (i === 0) bankroll -= amount;
  }
  const where = button === 0 ? 'You have the button.' : blinds[0]![0] === 0 ? 'You post the small blind.' : 'You post the big blind.';
  const state = withEquity({
    ...s,
    phase: 'betting',
    seats,
    board: [],
    deck,
    street: 0,
    raises: 1,
    turn: button, // three-handed, the button acts first before the flop
    button,
    log: [where],
    nextAt: env.now + AI_MS,
    bet: env.bet,
    returned: 0,
    result: null,
    lucky: false,
  });
  return { state, bankroll };
}

function position(s: HoldemState, i: number): string {
  if (s.button === i) return 'D';
  return (s.button + 1) % NAMES.length === i ? 'SB' : 'BB';
}

export const holdem: CasinoGame<HoldemState> = {
  id: 'holdem',
  usesLuck: true,
  name: "Hold'em",
  blurb: "Fixed-limit Texas Hold'em against two regulars. One hand a play.",
  // The bet is the big blind and a hand costs ~4 of them (swinging ~7), so
  // the limits are about a quarter of the other tables'. At the usual $5
  // minimum the early floors forced blinds far too big for the bankroll
  // and the ★ player busted ~80% of runs (sim, 2026-09-23).
  baseMinBet: 2,
  baseMaxBet: 25,
  specialPerk: 'Luck: a river that would beat you is sometimes dealt again.',
  jackpotRule: 'Win a showdown with four of a kind or better.',
  lockedHint: '(finish the hand)',
  create: () => ({
    phase: 'idle',
    seats: [],
    board: [],
    deck: [],
    street: 0,
    raises: 0,
    turn: -1,
    button: NAMES.length - 1, // the first hand deals you the button
    log: [],
    nextAt: 0,
    bet: 0,
    equity: 0,
    returned: 0,
    result: null,
    lucky: false,
  }),
  locked: (s) => s.phase === 'betting',
  options(s, env): TableOption[] {
    if (s.phase !== 'betting') return [{ id: 'deal', label: s.phase === 'done' ? 'Deal again' : 'Deal in' }];
    if (s.turn !== 0) return [{ id: 'wait', label: `${NAMES[s.turn]} is thinking` }];
    const me = s.seats[0]!;
    const top = topStreet(s);
    const toCall = top - me.street;
    const opts: TableOption[] = [];
    if (toCall === 0) opts.push({ id: 'call', label: 'Check' });
    else opts.push({ id: 'call', label: env.bankroll >= toCall ? `Call $${toCall}` : `All in $${env.bankroll}` });
    if (s.raises < CAP && env.bankroll >= toCall + betSize(s)) {
      opts.push({ id: 'raise', label: top === 0 ? `Bet $${betSize(s)}` : `Raise to $${top + betSize(s)}` });
    }
    if (toCall > 0) opts.push({ id: 'fold', label: 'Fold' });
    return opts;
  },
  describe(s, env, highlighted) {
    if (s.phase === 'idle') {
      return `Hold'em vs Mae and Sal. Your bet is the big blind; a hand costs ~4 blinds. Bets are 1 blind, 2 on turn and river. Rake ${Math.round(HOLDEM_TUNING.rake * 100)}% after a flop.`;
    }
    const me = s.seats[0]!;
    if (s.phase === 'done') {
      const r = s.result!;
      const net = s.returned - me.contrib;
      const lines: string[] = [];
      if (s.board.length) lines.push(`Board: ${cards(s.board)}`);
      if (r.how === 'showdown') {
        for (const [i, x] of s.seats.entries()) {
          lines.push(x.folded ? `${NAMES[i]}: folded` : `${NAMES[i]}: ${cards(x.hole)} ${holdemHandName(r.scores[i]!)}`);
        }
      } else lines.push(`You: ${cards(me.hole)}`, ...s.log.slice(-2));
      if (r.how === 'youFold') lines.push(`You fold. -$${me.contrib}`);
      else if (r.winners.includes(0) && r.winners.length > 1) lines.push(`Split pot. ${net >= 0 ? `+$${net}` : `-$${-net}`}`);
      else if (net > 0) lines.push(`${s.lucky ? '★ ' : ''}You win the pot! +$${net}`);
      else lines.push(`${NAMES[r.winners[0]!]} wins. -$${me.contrib}`);
      return lines.join('\n');
    }
    // Every line stays within the pane's 25 characters so none is rewrapped.
    const hand = holdemHandName(scoreSeven([...me.hole, ...s.board]));
    const lines = [`Pot $${pot(s)} · ${STREETS[s.street]}`];
    if (s.board.length) lines.push(cards(s.board));
    lines.push(`You: ${cards(me.hole)} · ${position(s, 0)}`, `${hand} · Win ${Math.round(s.equity * 100)}%`, ...s.log.slice(-3));
    const toCall = topStreet(s) - me.street;
    if (s.turn === 0 && highlighted === 'call' && toCall > 0) {
      lines.push(`Call breaks even at ${Math.round((toCall / (pot(s) + toCall)) * 100)}%`);
    }
    return lines.join('\n');
  },
  act(s, optionId, env) {
    if (optionId === 'deal' && s.phase !== 'betting') return deal(s, env);
    if (s.phase !== 'betting' || s.turn !== 0 || (optionId !== 'call' && optionId !== 'raise' && optionId !== 'fold')) {
      return { state: s, bankroll: env.bankroll };
    }
    const { s: after, paid } = applyAction(s, 0, optionId, env.bankroll);
    return advance(after, 0, env, env.bankroll - paid, []);
  },
  tick(s, env) {
    if (s.phase !== 'betting' || s.turn <= 0 || env.now < s.nextAt) return null;
    const i = s.turn;
    const x = s.seats[i]!;
    const toCall = topStreet(s) - x.street;
    // Once you're all in, the AIs only call or fold: nothing more you can win.
    const canRaise = s.raises < CAP && !s.seats[0]!.allIn;
    const choice = decide(x.hole, s.board, inHand(s).length - 1, toCall, pot(s), canRaise, OPPONENTS[i - 1]!, env.rng);
    const { s: after } = applyAction(s, i, choice, Infinity);
    return advance(after, i, env, env.bankroll, []);
  },
};
